import React, { useMemo, useCallback } from 'react';
import { ModelCard } from '../ModelCard';
import useBulkPermissions, { useModelIds } from '@hooks/useBulkPermissions';
import { ModelType } from '@constants/authorization';
import './ModelCardCollection.scss';

/**
 * Collection component that renders multiple ModelCards with bulk permission optimization
 *
 * @param {Array} models - Array of model objects to display
 * @param {string} modelType - Type of models (resources, categories, locations, etc.)
 * @param {string} displayType - Display mode ('grid' or 'list')
 * @param {Function} onModelClick - Click handler for individual models
 * @param {Function} onRefresh - Refresh handler after actions
 * @param {Function} getModelActions - Function to get custom actions for each model
 */
export const ModelCardCollection = React.memo(({
    models = [],
    modelType,
    displayType = 'grid',
    onModelClick,
    displayOptions = {},
    onRefresh,
    getModelActions
}) => {
    // Fetch bulk permissions for all models - pass models directly
    const { permissions, loading, error } = useBulkPermissions(models);

    // Helper function to get permissions for a specific model
    const getModelPermissions = useCallback((modelId) => {
        const modelPermissions = permissions[modelId];

        // With simplified API, permissions are directly available or undefined
        return modelPermissions || {};
    }, [permissions]);

    // Memoize the permission conversion function
    const getPermissionsForModel = useCallback((modelId) => {
        const modelPermissions = getModelPermissions(modelId);

        return {
            canUpdate: modelPermissions.update || false,
            canDelete: modelPermissions.delete || false,
            canBorrow: modelPermissions.borrow || false,
            canConsume: modelPermissions.consume || false,
            canRefill: modelPermissions.refill || false,
            canReserve: modelPermissions.reserve || false,
        };
    }, [getModelPermissions]);    // Memoize the rendered model cards to prevent unnecessary re-renders
    const renderedModelCards = useMemo(() => {
        return models.map((model) => {
            const permissions = getPermissionsForModel(model.id);
            const customActions = getModelActions ? getModelActions(model, permissions) : undefined;

            return (
                <ModelCard
                    key={model.id}
                    model={model}
                    displayType={displayType}
                    displayOptions={displayOptions}
                    onClick={onModelClick}
                    onRefresh={onRefresh}
                    actions={customActions}
                    permissions={permissions}
                />
            );
        });
    }, [models, displayType, onModelClick, onRefresh, getModelActions, getPermissionsForModel]);

    // If there are no models, render empty state
    if (!models.length) {
        return (
            <>
                <div className="empty-state">
                    <p>No {modelType} found</p>
                </div>
            </>
        );
    }

    return (
        <>
            {renderedModelCards}

            {/* Loading overlay when permissions are being fetched */}
            {loading && (
                <div className="model-card-collection__loading">
                    <div className="loading-spinner">Loading permissions...</div>
                </div>
            )}

            {/* Error display if permission loading fails */}
            {error && (
                <div className="model-card-collection__error">
                    <p>Error loading permissions. Some actions may not be available.</p>
                </div>
            )}
        </>
    );
});

// Custom comparison function for React.memo to prevent unnecessary re-renders
const arePropsEqual = (prevProps, nextProps) => {
    // Check if arrays have the same length and same IDs
    if (prevProps.models.length !== nextProps.models.length) {
        return false;
    }

    // Check if model IDs are the same
    const prevIds = prevProps.models.map(m => m.id).sort();
    const nextIds = nextProps.models.map(m => m.id).sort();
    if (JSON.stringify(prevIds) !== JSON.stringify(nextIds)) {
        return false;
    }

    // Check other simple props
    if (prevProps.displayType !== nextProps.displayType) {
        return false;
    }

    if (prevProps.onModelClick !== nextProps.onModelClick) {
        return false;
    }

    if (prevProps.onRefresh !== nextProps.onRefresh) {
        return false;
    }

    if (prevProps.getModelActions !== nextProps.getModelActions) {
        return false;
    }

    return true;
};

export default React.memo(ModelCardCollection, arePropsEqual);
